import {FC, useCallback, useEffect, useRef, useState} from "react";
import Quotes from "./Quotes.tsx";
import { useWidgetData } from "../../hooks/useWidgetData.ts";
import { getSSRTranslation } from "../../../i18n.ts";
import { FeedQueryResult, LightboxItem, LightboxLanguageText } from "../../net/softbox-types.ts";
import "../WidgetCard/WidgetCard.scss";
import { useTranslation } from 'react-i18next';

interface QuoteData {
  text: string;
  attribution?: string;
}

// Extend LightboxItem to include quote-specific properties
interface QuoteItem extends LightboxItem {
  quote?: {
    quotetext: LightboxLanguageText;
    attribution: string;
  };
}

export interface QuotesWrapperProps {
  feedType?: string
  dynamicFeed?: boolean
}

const QuotesWrapper: FC = ({ feedType = 'quotes', dynamicFeed = false }: QuotesWrapperProps) => {
  const { i18n, t } = useTranslation();
  const [currentQuote, setCurrentQuote] = useState<QuoteData>({ text: '', attribution: '' });
  const unseenQuotesRef = useRef<QuoteData[]>([]);

  // This is our "class method" - the data processor specific to quotes
  const processQuoteData = useCallback((feedData: FeedQueryResult): QuoteData[] => {
    if (!feedData?.news?.items) {
      return [];
    }

    // Extract quote data from the items
    const quoteData: QuoteData[] = feedData.news.items.map((item) => {
      // Cast item to QuoteItem to access quote-specific properties
      const quoteItem = item as QuoteItem;
      // Extract quote text and attribution from the item
      const quoteText = quoteItem.quote?.quotetext ? getSSRTranslation(quoteItem.quote.quotetext, i18n) : getSSRTranslation(item.title, i18n);
      const attribution = quoteItem.quote?.attribution || '';

      return {
        text: quoteText || '',
        attribution: attribution
      };
    }).filter((quote) => {return quote.text && quote.text.trim().length > 0});

    return quoteData;
  }, []);

  // Use our generic hook - this is the "inherited" functionality
  const { data: quotes, loading, error, refetch } = useWidgetData<QuoteData>(feedType, processQuoteData, dynamicFeed);

  const updateQuote = useCallback(() => {
    // If unseenQuotesRef is empty, reset it from the original quotes
    if (unseenQuotesRef.current.length === 0) {
      unseenQuotesRef.current = [...quotes];
    }

    const newQuote = unseenQuotesRef.current.pop() ?? { text: '', attribution: '' };
    setCurrentQuote(newQuote);
  }, [quotes]);

  // Set up quotes when they change
  useEffect(() => {
    if (quotes.length > 0) {
      unseenQuotesRef.current = [...quotes];
      updateQuote(); // Set initial quote
    }
  }, [quotes, updateQuote]);

  return (
    <div className="widget-card" data-testid="test-widget-card">
      <h2 className="widget-card__title header-md">{t('Quote of the Day')}</h2>
      <div className="widget-card__content">
        {/* Show loading state */}
        {loading && <Quotes getNewQuote={() => {}} quote={t("Loading quotes...")} />}

        {/* Show error state */}
        {error && <Quotes getNewQuote={() => {return refetch()}} quote={`${t("Error")}: ${error}. ${t("Click to retry")}.`} />}

        {/* Show quote content */}
        {!loading && !error && quotes.length > 0 && (
          <Quotes getNewQuote={updateQuote} quote={currentQuote.text} attribution={currentQuote.attribution} />
        )}

        {/* No quotes available */}
        {!loading && !error && quotes.length === 0 && (
          <Quotes getNewQuote={() => {return refetch()}} quote={t("No quotes available. Click to retry.")} />
        )}
      </div>
    </div>
  );
};

export default QuotesWrapper;
